import { Box, Typography, Tooltip, tooltipClasses, TooltipProps } from "@mui/material"
import { ProductCard } from "./ProductCard"
import { styled } from "@mui/material/styles"
import { useState, useEffect } from "react"
import { makeStyles } from "tss-react/mui"
import { TodaysOfferDetails } from "./TodaysOfferDetails"
import { fetchMenuOfWeek, GetMenuOfWeekResponse, UserWeekMenu } from "../../../weekMenuApi"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { selectKitchen } from "../../../../../nonAdminUserSelectors"
import { WeekMenuProduct } from "../../../weekMenuTypes"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useMoney } from "../../../../../../currency/useMoney"
import { Stack } from "@mui/system"
import { DaySelector } from "./components/DaySelector"
import { MenuSkeleton } from "./components/MenuSkeleton"
import { NoMenu } from "./components/NoMenu"
import { UserDashboardErrorFallbackCard } from "../../UserDashboardErrorFallbackCard"
import { QueryErrorBoundary } from "../../../../../../../core/api/components/QueryErrorBoundary"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { every, isEmpty, some } from "lodash-es"
import { ProductCardInfo } from "./components/ProductCardInfo"

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: "1rem 1.25rem",
        borderRadius: ".75rem",
        backgroundColor: "white",
        marginBottom: "3rem",
        [theme.breakpoints.up("xl")]: {
            padding: "2.25rem",
        },
    },
    newsGrid: {
        display: "flex",
        gap: "1.25rem",
        overflowX: "auto",
        paddingBottom: "0.5rem",
        "&::-webkit-scrollbar": {
            display: "none",
        },
    },
    showMenuLink: {
        marginTop: "1rem",
        textAlign: "right",
        fontWeight: 500,
        width: "fit-content",
        color: theme.palette.primary.main,
        textDecoration: "none",
        fontSize: "0.875rem",
        "&:hover": {
            textDecoration: "underline",
        },
        cursor: "pointer",
    },
    productsListContainer: {
        paddingTop: "0.25rem",
        width: "100%",
        display: "flex",
        gap: "1.5rem",
        maxWidth: "40rem",
    },
    errorCard: {
        boxShadow: "none",
        marginBottom: "3rem",
    },
    errorContent: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
    },
}))

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: "rgba(0, 0, 0, 0.87)",
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}))

const transformUserWeekMenu = (data: GetMenuOfWeekResponse) => data.menues

export function SafeTodayMenuSection() {
    const { classes } = useStyles()
    const selectedKitchen = useAppSelector(selectKitchen)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [selectedDays, setSelectedDays] = useState<WeekMenuProduct[]>()
    const [showDetailsModal, setShowDetailsModal] = useState(false)
    const moneyFactory = useMoney({})
    const dateTimeFactory = useDateTime()

    const { data: weekMenus, isLoading } = useApiQuery(fetchMenuOfWeek, {
        queryName: UserWeekMenu.USER_WEEK_MENU,
        dependencies: {
            canteenId: selectedKitchen?.id ?? 0,
        },
        enabled: Boolean(selectedKitchen?.id),
        select: transformUserWeekMenu,
        useErrorBoundary: true,
    })

    const isWeekMenu = Array.isArray(weekMenus) && weekMenus.length > 0

    useEffect(() => {
        if (isWeekMenu) {
            const todayDate = dateTimeFactory.now().toISODate()
            const todayIndex = weekMenus.findIndex((menu) => menu.date === todayDate)
            setCurrentIndex(todayIndex !== -1 ? todayIndex : 0)
        }
    }, [isWeekMenu, weekMenus])

    const handleDaySelect = (index: number) => {
        const menu = weekMenus?.[index]
        if (menu && menu.products?.length > 0) {
            setCurrentIndex(index)
        }
    }

    const currentMenu = isWeekMenu ? weekMenus[currentIndex] : null
    const hasProducts = currentMenu && currentMenu?.products?.length > 0

    const imageMode = every(currentMenu?.products, (p) => isEmpty(p.imageUrl))
        ? "none" // no images at all → hide
        : some(currentMenu?.products, (p) => isEmpty(p.imageUrl))
        ? "mixed" // some have images → use placeholde
        : "unknown"

    if (!isWeekMenu) {
        return null
    }

    return (
        <Box className={classes.container}>
            <LocalizedStrict id="user-home-canteen-today-menu">
                <Typography variant="h5" fontWeight="bold" marginBottom="1.5rem">
                    Today's Menu
                </Typography>
            </LocalizedStrict>

            {isWeekMenu && (
                <DaySelector weekMenus={weekMenus} currentIndex={currentIndex} onDaySelect={handleDaySelect} />
            )}

            <Box className={classes.newsGrid}>
                {isLoading ? (
                    <MenuSkeleton />
                ) : (
                    <Box className={classes.productsListContainer}>
                        {hasProducts ? (
                            currentMenu.products.map((product: WeekMenuProduct) => (
                                <LightTooltip
                                    key={product.id}
                                    title={
                                        <Stack sx={{ padding: "0.5rem" }}>
                                            <ProductCardInfo resource={product} />
                                        </Stack>
                                    }
                                    placement="top"
                                >
                                    <Box>
                                        <ProductCard
                                            imageMode={imageMode}
                                            onCardClick={() => {
                                                setSelectedDays(currentMenu.products)
                                                setShowDetailsModal(true)
                                            }}
                                            resource={product}
                                        />
                                    </Box>
                                </LightTooltip>
                            ))
                        ) : (
                            <NoMenu />
                        )}
                    </Box>
                )}
            </Box>

            {hasProducts && (
                <Stack justifyContent="end" direction="row">
                    <LocalizedStrict id="user-home-canteen-today-menu-show-menu">
                        <Typography
                            className={classes.showMenuLink}
                            variant="body2"
                            onClick={() => {
                                setSelectedDays(currentMenu.products)
                                setShowDetailsModal(true)
                            }}
                        >
                            Show menu signage...
                        </Typography>
                    </LocalizedStrict>
                </Stack>
            )}

            {showDetailsModal && (
                <TodaysOfferDetails
                    imageMode={imageMode}
                    displayDate={isWeekMenu ? weekMenus[currentIndex].displayDate : ""}
                    isOpen={showDetailsModal}
                    onClose={() => setShowDetailsModal(false)}
                    weeklyOffers={selectedDays}
                />
            )}
        </Box>
    )
}

const TodaysMenuErrorFallback = () => {
    const { classes } = useStyles()

    return (
        <Box className={classes.container}>
            <Box marginBottom="3rem">
                <UserDashboardErrorFallbackCard classNames={classes.errorCard} contentClassNames={classes.errorContent}>
                    <LocalizedStrict id="user-home-canteen-today-menu">
                        <Typography variant="h5" fontWeight="bold" marginBottom="1.5rem">
                            Today's Menu
                        </Typography>
                    </LocalizedStrict>
                </UserDashboardErrorFallbackCard>
            </Box>
        </Box>
    )
}

function TodayMenuSection() {
    return (
        <QueryErrorBoundary Fallback={TodaysMenuErrorFallback}>
            <SafeTodayMenuSection />
        </QueryErrorBoundary>
    )
}

export { TodayMenuSection }
